import React from "react";
import { Box, Button, ButtonGroup, IconButton, Tooltip, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import NearMeIcon from "@mui/icons-material/NearMe";
import TimelineIcon from "@mui/icons-material/Timeline";
import { HEADER_ICON_BUTTON_SX } from "app/components/Layout/filterToolbarStyles";
import { MapScreenViewMode } from "app/types/mapScreen";

const VIEW_OPTIONS: Array<{
  id: MapScreenViewMode;
  label: string;
  icon: React.ReactNode;
}> = [
  { id: "live", label: "Live view", icon: <MyLocationIcon sx={{ fontSize: 16 }} /> },
  { id: "select", label: "Select mode", icon: <NearMeIcon sx={{ fontSize: 16 }} /> },
  { id: "timeline", label: "Timeline view", icon: <TimelineIcon sx={{ fontSize: 16 }} /> },
];

type MapScreenToolbarProps = {
  viewMode: MapScreenViewMode;
  onViewModeChange: (mode: MapScreenViewMode) => void;
  onClose: () => void;
};

export function MapScreenToolbar({
  viewMode,
  onViewModeChange,
  onClose,
}: MapScreenToolbarProps) {
  return (
    <Box
      sx={{
        px: 2,
        py: 1,
        bgcolor: "#fff",
        borderBottom: "1px solid #e5e9ef",
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        flexWrap: "wrap",
        flexShrink: 0,
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#0f172a" }}>
        Map view
      </Typography>

      <ButtonGroup
        variant="outlined"
        sx={{
          bgcolor: "#eef2f6",
          borderRadius: "10px",
          p: 0.35,
          boxShadow: "inset 0 1px 2px rgba(15, 23, 42, 0.06)",
          "& .MuiButton-root": {
            textTransform: "none",
            border: "none",
            px: 1.35,
            py: 0.7,
            fontSize: "0.78rem",
            fontWeight: 600,
            gap: 0.5,
            borderRadius: "8px !important",
            minWidth: 0,
          },
          "& .MuiButton-root:not(:last-of-type)": {
            borderRight: "none",
          },
        }}
      >
        {VIEW_OPTIONS.map((option) => {
          const selected = viewMode === option.id;
          return (
            <Button
              key={option.id}
              onClick={() => onViewModeChange(option.id)}
              startIcon={option.icon}
              sx={{
                bgcolor: selected ? "#334155" : "transparent",
                color: selected ? "#fff" : "#64748b",
                boxShadow: selected ? "0 2px 6px rgba(15, 23, 42, 0.18)" : "none",
                "&:hover": {
                  bgcolor: selected ? "#1e293b" : "rgba(255,255,255,0.55)",
                },
                "& .MuiButton-startIcon": {
                  color: "inherit",
                },
              }}
            >
              {option.label}
            </Button>
          );
        })}
      </ButtonGroup>

      <Typography
        variant="caption"
        sx={{ color: "#64748b", ml: { xs: 0, md: 1 } }}
      >
        Orange pins are unassigned. Blue pins are already on a route.
      </Typography>

      <Tooltip title="Close map view">
        <IconButton
          aria-label="Close map view"
          size="small"
          onClick={onClose}
          sx={{ ...HEADER_ICON_BUTTON_SX, ml: "auto" }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
}
